// Maps Meta's account_status_label (set by the backend in
// MetaMarketingApi::GraphClient::ACCOUNT_STATUS_LABELS) to the badge displayed
// in the assignments modal and the per-group tab.
//
// Multiple raw statuses can collapse into one badge: unsettled,
// pending_settlement, and in_grace_period all surface as Restricted because
// they share the same user-facing meaning ("billing-related access limit you
// can recover from"). See the Figma "Statuses UI and Warning Text" chart for
// the source of truth.

export type BadgeKind = 'success' | 'warning' | 'danger';
export type BadgeName = 'active' | 'restricted' | 'in_review' | 'pending_closure' | 'disabled' | 'closed';

export interface StatusBadge {
  name: BadgeName;
  kind: BadgeKind;
  // Active is the only badge without an accompanying warning line.
  hasWarning: boolean;
}

const BADGES: Record<BadgeName, { kind: BadgeKind; hasWarning: boolean }> = {
  active: { kind: 'success', hasWarning: false },
  restricted: { kind: 'warning', hasWarning: true },
  in_review: { kind: 'warning', hasWarning: true },
  pending_closure: { kind: 'warning', hasWarning: true },
  disabled: { kind: 'danger', hasWarning: true },
  closed: { kind: 'danger', hasWarning: true }
};

// Covers every value MetaMarketingApi::GraphClient::ACCOUNT_STATUS_LABELS can
// emit except 'unknown' (Meta returns a code we don't recognize) — that one
// falls through to undefined and renders no badge.
const STATUS_LABEL_TO_BADGE: Record<string, BadgeName> = {
  active: 'active',
  disabled: 'disabled',
  unsettled: 'restricted',
  pending_risk_review: 'in_review',
  pending_settlement: 'restricted',
  in_grace_period: 'restricted',
  pending_closure: 'pending_closure',
  closed: 'closed',
  // any_active / any_closed are Meta's composite query statuses — they don't
  // appear on individual ad accounts in practice, but map defensively so a
  // backend label change doesn't silently drop the badge.
  any_active: 'active',
  any_closed: 'closed'
};

export function getStatusBadge(accountStatusLabel: string | undefined): StatusBadge | undefined {
  if (!accountStatusLabel) return undefined;
  const name = STATUS_LABEL_TO_BADGE[accountStatusLabel];
  if (!name) return undefined;
  return { name, ...BADGES[name] };
}
